<script setup>
import { ref, onMounted, computed } from 'vue';
import L from 'leaflet';
import { MarkerClusterGroup } from 'leaflet.markercluster/src';
import BaseFilters from './BaseFilters.vue';
import BaseTooltipCard from './BaseTooltipCard.vue';
import { calculateBounds } from '../utils/calculate-bounds';
import { calculateCenter } from '../utils/calculate-center';
import { makeMarkerIcon } from '../utils/make-marker-icon';
import { makeTooltipCard } from '../utils/make-tooltip-card';
import { makeFilterButtonHTML } from '../utils/make-filter-button-html';
import { makeListViewButtonHTML } from '../utils/make-list-view-button-html';
import { createMarkerHighlighter } from '../utils/selected-marker';
import { createPolygonSelector } from '../utils/selected-polygon';
import BaseSearchInput from './BaseSearchInput.vue';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import '../utils/map-window-api';
import { getColorFromMarker } from '../utils/get-color-from-marker';

const props = defineProps({
	datasets: {
		type: Array,
		default: () => [],
	},
	selectedDatasets: {
		type: Array,
		required: true
	},
	settings: {
		type: Array,
		default: () => [],
	},
	primaryColor: {
		type: String,
		required: true,
		default: '#328725',
	},
	tileLayerUri: {
		type: String,
		required: true,
	},
});

const tooltipCard = ref(null);
const showFiltersCard = ref(false);
const mapRef = ref(null);
// Reference to this instance's map DOM element, so multiple maps can coexist on one page.
const mapContainer = ref(null);
// Polygon selection state scoped to this map instance.
const { selectOverlappingPolygon, resetPolygonSelection } = createPolygonSelector();
// Marker highlight state scoped to this map instance.
const { highlightSelectedMarker, resetMarkers, activeMarkerRef } = createMarkerHighlighter();
const clusters = ref([]);
const searchQuery = ref('');
const resultsCount = ref(0);
const lastSearchMarker = ref(null);
const clusterOptions = {
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  zoomToBoundsOnClick: true,
  maxClusterRadius: 40,
  disableClusteringAtZoom: 15,
  iconCreateFunction: (cluster) => {
    const count = cluster.getChildCount();
    return L.divIcon({
      html: `
        <div class="owc-openkaarten-streetmap__cluster-group">
          <div class="owc-openkaarten-streetmap__cluster-group__circle">
            <span class="owc-openkaarten-streetmap__cluster-group__count">${count}</span>
          </div>
        </div>
      `,
      className: '',
      iconSize: L.point(40, 40),
    });
  },
};

const closeTooltipCard = () => {
	tooltipCard.value = null;
  resetMarkers(mapContainer.value);
  resetPolygonSelection();
  mapContainer.value?.focus();
};

const closeFilters = () => {
	showFiltersCard.value = false;
};

const datasetChange = (id, checked) => {
	console.log('datasetChange called with:', id, checked);
	if (!id) return null;
	emit('datasetChange', id, checked);
	const map = mapRef.value;
	const layers = clusters.value;
	const datalayerCluster = layers.find(
		(layer) => layer.id === id
	)?.cluster;
	const existsOnMap = map.hasLayer(datalayerCluster);
	if (checked) {
		if (existsOnMap) return null;
		map.addLayer(datalayerCluster);
	} else {
		map.removeLayer(datalayerCluster);
	}
};

// Move attachEvents outside of initializeMap
const attachEvents = (marker, location, set) => {
  marker.on('click', (e) => {
    highlightSelectedMarker(marker, mapContainer.value);

    if (location.geometry?.type !== 'Polygon') {
      resetPolygonSelection();
      tooltipCard.value = makeTooltipCard(location, set);
      return;
    }

    // For polygons, select overlapping polygon and use its data for tooltip.
    const selectedLayer = selectOverlappingPolygon(mapRef.value, e.latlng, location.properties?.marker);

    // Use the selected layer's data for the tooltip if available.
    if (selectedLayer && selectedLayer._featureData && selectedLayer._datasetData) {
      tooltipCard.value = makeTooltipCard(selectedLayer._featureData, selectedLayer._datasetData);
    } else {
      // Fallback to original data if layer data not found.
      tooltipCard.value = makeTooltipCard(location, set);
    }
  });

  marker.on('keydown', ({ originalEvent }) => {
    if (originalEvent.key === 'Enter') {
      tooltipCard.value = makeTooltipCard(location, set);
      highlightSelectedMarker(marker, mapContainer.value);
    }
  });
};

/**
 * Build the correct Leaflet layer for a GeoJSON feature.
 *
 * Synchronous by design: marker icons are applied as lightweight placeholders immediately
 * and upgraded to the processed SVG in the background once `fetchAndProcessSvg` resolves.
 * This keeps map initialization off the critical path of the SVG network requests, so the
 * tile layer and all markers are visible right away instead of blocking on a potentially
 * slow `/opengemeenten-iconenset/` fetch.
 *
 * @author Merel Voorn - van Uffelen
 *
 * @param {object} feature  A single GeoJSON feature from the dataset.
 * @param {object} dataset  The parent dataset — retained for debug logging / future use.
 * @returns {object} A Leaflet layer ready to be added to a cluster / map.
 */
const createLayer = (feature, dataset) => {
  console.log('Creating layer for feature:', feature);
  if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
    return new L.GeoJSON(feature, {
      style: () => {
        const color = getColorFromMarker(feature.properties?.marker, props.primaryColor);
        return {
          color: color,
          fillColor: color,
          fillOpacity: 0.2,
          weight: 2
        };
      }
    });
  }

  // Handle MultiPoint features.
  if (feature.geometry.type === 'MultiPoint') {
    const markers = L.featureGroup();
    for (const coord of feature.geometry.coordinates) {
      const latlng = L.latLng(coord[1], coord[0]);
      // Get the placeholder icon synchronously, then upgrade it per-marker when the SVG lands.
      const { icon, ready } = makeMarkerIcon(L, {
        marker: feature.properties?.marker,
        defaultColor: props.primaryColor,
      });
      const marker = new L.Marker(latlng, { icon });
      ready.then((upgradedIcon) => {
        if (upgradedIcon) {
          marker.setIcon(upgradedIcon);
        }
      });
      markers.addLayer(marker);
    };
    return markers;
  }

  // Handle regular points using GeoJSON.
  // All markers in this feature share one icon config, so we create one placeholder up front
  // and upgrade every generated marker at once when the SVG fetch resolves.
  const { icon, ready } = makeMarkerIcon(L, {
    marker: feature.properties?.marker,
    defaultColor: props.primaryColor,
  });

  const pendingMarkers = [];

  const layer = new L.GeoJSON(feature, {
    pointToLayer: (feature, latlng) => {
      const marker = new L.Marker(latlng, { icon });
      pendingMarkers.push(marker);
      return marker;
    }
  });

  ready.then((upgradedIcon) => {
    if (!upgradedIcon) {
      return;
    }
    pendingMarkers.forEach((marker) => marker.setIcon(upgradedIcon));
  });

  return layer;
};

const initializeMap = async (datasets, settings) => {
	console.log('Initializing map with datasets:', datasets);
	console.log('Initializing map with settings:', settings);
	const bounds = calculateBounds(datasets);
	let lat = settings.default_lat ?? '52.1326'; // Default lat (Netherlands center)
	let long = settings.default_lng ?? '5.2913'; // Default long (Netherlands center)
// Default_zoom from the settings is only a fallback for when there are
// no markers (or all markers are in exactly the same place).
// As soon as there is a bounding box, calculateBounds automatically derives a
// suitable zoom and we overwrite it further with map.fitBounds().
// This ensures that the default_zoom setting is only used when there are no features to show, or when all features have the same coordinates (and thus no bounds can be calculated).
	let zoom = settings.default_zoom ?? 12;

	if ( !bounds ) {
		console.info("No valid bounds could be calculated from the datasets.");
		console.log("Center calculated as:", { lat, long, zoom });
	} else {
		const center = calculateCenter(bounds);
		lat = center.lat;
		long = center.long;
	}

	const config = {
		centerX: lat,
		centerY: long,
		minimumZoom: 4,
		maximumZoom: 18,
		defaultZoom: zoom,
		enableHomeControl: true,
		enableZoomControl: true,
		enableBoxZoomControl: true
	};

	const map = new L.Map(mapContainer.value, {
		center: [config.centerX, config.centerY],
		zoom: config.defaultZoom,
		minZoom: config.minimumZoom,
		maxZoom: config.maximumZoom,
		zoomControl: false,
		boxZoom: config.enableBoxZoomControl,
		defaultExtentControl: config.enableHomeControl,
	});

	map.setView([config.centerX, config.centerY], config.defaultZoom);

  if (config.enableZoomControl) {
    L.control.zoom({ position: 'bottomright' }).addTo(map);
  }

  // Add the tile layer *before* building the marker clusters so the base map is visible
  // immediately, even while individual marker SVGs are still being fetched in the background.
  // Previously this lived at the bottom of this function, which caused the whole map to
  // appear blank whenever an SVG fetch was slow.
  const tileLayerUri = new L.TileLayer(props.tileLayerUri);
  map.addLayer(tileLayerUri);

  const filteredDatasets = datasets.filter((dataset) => props.selectedDatasets.includes(dataset.id));

  const groupedMarkerClusters = await Promise.all(
    filteredDatasets.map(async (dataset) => {
      // Create a pane for this dataset.
      const pane = map.createPane(dataset.title.replace(' ', '_'));
      const cluster = L.markerClusterGroup({
        ...clusterOptions,
        clusterPane: pane
      });

			// Check if features is empty. If so, return early.
			if (!dataset.features || dataset.features.length === 0) {
				return {
					id: dataset.id,
					cluster,
          hasFeatures: false
				};
			}

			if (dataset.features.constructor !== Array) {
				dataset.features = [dataset.features];
			}

      for (const feature of dataset.features) {
        // createLayer is now synchronous: placeholders are applied immediately and each
        // marker upgrades itself when its SVG finishes fetching in the background.
        const layer = createLayer(feature, dataset);
        // Store feature and dataset data on the layer for later retrieval.
        layer._featureData = feature;
        layer._datasetData = dataset;
        // Also store on child layers if this is a layer group (like GeoJSON).
        if (layer.getLayers) {
          layer.eachLayer(childLayer => {
            childLayer._featureData = feature;
            childLayer._datasetData = dataset;
          });
        }
        attachEvents(layer, feature, dataset);
        cluster.addLayer(layer);
      }

			return {
				id: dataset.id,
				cluster,
        hasFeatures: true
			};
		})
  );

	L.Control.DataLayerFilters = L.Control.extend({
		options: {
			position: 'topleft',
		},
		onAdd: function () {
			const btn = L.DomUtil.create(
				'button',
				'leaflet-control-filters'
			);
			L.DomEvent.addListener(
				btn,
				'click',
				L.DomEvent.stopPropagation
			)
				.addListener(btn, 'click', L.DomEvent.preventDefault)
				.addListener(btn, 'click', function () {
					showFiltersCard.value = !showFiltersCard.value;
				});

			const title = 'Filter';
			btn.title = title;
			btn.innerHTML = makeFilterButtonHTML(
				'Filter',
				props.primaryColor
			);

			return btn;
		},
	});

	L.Control.ListViewToggle = L.Control.extend({
		options: {
			position: 'topleft',
		},
		onAdd: function () {
			const btn = L.DomUtil.create('button', 'leaflet-control-list-view');
			L.DomEvent.addListener(btn, 'click', L.DomEvent.stopPropagation)
				.addListener(btn, 'click', L.DomEvent.preventDefault)
				.addListener(btn, 'click', function () {
					emit('toggleView');
				});

			const title = 'Lijst';
			btn.title = title;
			btn.innerHTML = makeListViewButtonHTML(title, props.primaryColor);

			return btn;
		},
	});

	// Tile layer is already added near the top of initializeMap so the base map renders immediately.
	const datalayerFilters = new L.Control.DataLayerFilters();
	const listViewToggle = new L.Control.ListViewToggle();

  if (groupedMarkerClusters?.some(item => item.hasFeatures)) {
    map.addControl(listViewToggle);
  }

	if (groupedMarkerClusters?.length > 0) {
		map.addControl(datalayerFilters);
    showFiltersCard.value = true;
	}

  // Fit map view to marker bounds.
  if (bounds) {
    map.fitBounds([
      [bounds.minLat, bounds.minLong],
      [bounds.maxLat, bounds.maxLong],
    ], {
      padding: [40, 40],
      maxZoom: 15
    });

    // Calculate zoom based on bounds.
    zoom = map.getZoom();
    console.log('Center calculated as:', { lat, long, zoom });
  }

	groupedMarkerClusters.forEach(({ cluster }) => {
		map.addLayer(cluster);
    cluster.on('animationend layeradd spiderfied', () => {
      if (activeMarkerRef.value) {
        highlightSelectedMarker(activeMarkerRef.value, mapContainer.value);
      }
    });
	});
	clusters.value = groupedMarkerClusters;
	mapRef.value = map;

    if (window.openkaarten && typeof window.openkaarten.registerMap === 'function') {
		window.openkaarten.registerMap(map);
    }

	window.dispatchEvent(new CustomEvent('openkaarten:map-ready', {
		detail: { map }
	}));
};

const emit = defineEmits(['toggleView', 'datasetChange']);

onMounted(async () => {
	if (mapContainer.value) {
		await initializeMap(props.datasets, props.settings);
	}
});

// Add search handler
const handleSearch = async (query) => {
  searchQuery.value = query;
  const map = mapRef.value;

  if (!map || !query) return;

  try {
    const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
    );
    const results = await response.json();

    if (results.length > 0) {
      const { lat, lon } = results[0]; // Take the first (most relevant) match.
      const latLng = L.latLng(parseFloat(lat), parseFloat(lon));
      const targetIcon = L.icon({
        iconUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAFgUlEQVR4Aa1XA5BjWRTN2oW17d3YaZtr2962HUzbDNpjszW24mRt28p47v7zq/bXZtrp/lWnXr337j3nPCe85NcypgSFdugCpW5YoDAMRaIMqRi6aKq5E3YqDQO3qAwjVWrD8Ncq/RBpykd8oZUb/kaJutow8r1aP9II0WmLKLIsJyv1w/kqw9Ch2MYdB++12Onxee/QMwvf4/Dk/Lfp/i4nxTXtOoQ4pW5Aj7wpici1A9erdAN2OH64x8OSP9j3Ft3b7aWkTg/Fm91siTra0f9on5sQr9INejH6CUUUpavjFNq1B+Oadhxmnfa8RfEmN8VNAsQhPqF55xHkMzz3jSmChWU6f7/XZKNH+9+hBLOHYozuKQPxyMPUKkrX/K0uWnfFaJGS1QPRtZsOPtr3NsW0uyh6NNCOkU3Yz+bXbT3I8G3xE5EXLXtCXbbqwCO9zPQYPRTZ5vIDXD7U+w7rFDEoUUf7ibHIR4y6bLVPXrz8JVZEql13trxwue/uDivd3fkWRbS6/IA2bID4uk0UpF1N8qLlbBlXs4Ee7HLTfV1j54APvODnSfOWBqtKVvjgLKzF5YdEk5ewRkGlK0i33Eofffc7HT56jD7/6U+qH3Cx7SBLNntH5YIPvODnyfIXZYRVDPqgHtLs5ABHD3YzLuespb7t79FY34DjMwrVrcTuwlT55YMPvOBnRrJ4VXTdNnYug5ucHLBjEpt30701A3Ts+HEa73u6dT3FNWwflY86eMHPk+Yu+i6pzUpRrW7SNDg5JHR4KapmM5Wv2E8Tfcb1HoqqHMHU+uWDD7zg54mz5/2BSnizi9T1Dg4QQXLToGNCkb6tb1NU+QAlGr1++eADrzhn/u8Q2YZhQVlZ5+CAOtqfbhmaUCS1ezNFVm2imDbPmPng5wmz+gwh+oHDce0eUtQ6OGDIyR0uUhUsoO3vfDmmgOezH0mZN59x7MBi++WDL1g/eEiU3avlidO671bkLfwbw5XV2P8Pzo0ydy4t2/0eu33xYSOMOD8hTf4CrBtGMSoXfPLchX+J0ruSePw3LZeK0juPJbYzrhkH0io7B3k164hiGvawhOKMLkrQLyVpZg8rHFW7E2uHOL888IBPlNZ1FPzstSJM694fWr6RwpvcJK60+0HCILTBzZLFNdtAzJaohze60T8qBzyh5ZuOg5e7uwQppofEmf2++DYvmySqGBuKaicF1blQjhuHdvCIMvp8whTTfZzI7RldpwtSzL+F1+wkdZ2TBOW2gIF88PBTzD/gpeREAMEbxnJcaJHNHrpzji0gQCS6hdkEeYt9DF/2qPcEC8RM28Hwmr3sdNyht00byAut2k3gufWNtgtOEOFGUwcXWNDbdNbpgBGxEvKkOQsxivJx33iow0Vw5S6SVTrpVq11ysA2Rp7gTfPfktc6zhtXBBC+adRLshf6sG2RfHPZ5EAc4sVZ83yCN00Fk/4kggu40ZTvIEm5g24qtU4KjBrx/BTTH8ifVASAG7gKrnWxJDcU7x8X6Ecczhm3o6YicvsLXWfh3Ch1W0k8x0nXF+0fFxgt4phz8QvypiwCCFKMqXCnqXExjq10beH+UUA7+nG6mdG/Pu0f3LgFcGrl2s0kNNjpmoJ9o4B29CMO8dMT4Q5ox8uitF6fqsrJOr8qnwNbRzv6hSnG5wP+64C7h9lp30hKNtKdWjtdkbuPA19nJ7Tz3zR/ibgARbhb4AlhavcBebmTHcFl2fvYEnW0ox9xMxKBS8btJ+KiEbq9zA4RthQXDhPa0T9TEe69gWupwc6uBUphquXgf+/FrIjweHQS4/pduMe5ERUMHUd9xv8ZR98CxkS4F2n3EUrUZ10EYNw7BWm9x1GiPssi3GgiGRDKWRYZfXlON+dfNbM+GgIwYdwAAAAASUVORK5CYII=',
        iconSize: [15, 24],
        iconAnchor: [0, 24],
        popupAnchor: [8, -7]
      });

      if (map && map._loaded) {
        map.once('zoomend', () => {
          if (lastSearchMarker.value) {
            map.removeLayer(lastSearchMarker.value);
          }
          const targetMarker = L.marker(latLng, { icon: targetIcon });
          targetMarker.addTo(map);
          targetMarker.bindPopup("Gevonden locatie");
          lastSearchMarker.value = targetMarker;
        });
        map.flyTo(latLng, 15, { animate: true, duration: 1 });
      }
      resultsCount.value = results.length ? 1 : 0
    }
  } catch (error) {
    console.error("Error in retrieving location:", error);
  }
};
</script>

<template v-cloak>
	<div
    :class="{
      'owc-openkaarten-streetmap__map': true,
      'filters-open': showFiltersCard,
      }"
	>
		<div class="owc-openkaarten-streetmap__controls">
			<BaseSearchInput
        :placeholder="'Zoek op straat en/of plaats of postcode'"
				:primary-color="primaryColor"
        :results-count="resultsCount"
				@search="handleSearch"
			/>
		</div>
		<div ref="mapContainer" class="owc-openkaarten-streetmap__map-canvas"></div>
    <Transition name="fade">
      <div
          v-if="showFiltersCard"
          class="owc-openkaarten-streetmap__overlay"
      ></div>
    </Transition>
		<Transition name="slide">
			<BaseFilters
				v-if="datasets && datasets.length > 0 && showFiltersCard"
				:open="showFiltersCard"
				:datasets="datasets.filter((set) => set.features?.length)"
				:selectedDatasets="selectedDatasets"
				:primaryColor="primaryColor"
				:mapElement="mapContainer"
				@closeFilters="closeFilters"
				@datasetChange="datasetChange"
			/>
		</Transition>
		<BaseTooltipCard
			v-if="tooltipCard"
			:key="tooltipCard.datasetId"
			:id="tooltipCard.datasetId"
			:tooltipArray="tooltipCard.tooltipArray"
			:properties="tooltipCard.properties"
			:primaryColor="primaryColor"
			@closeCard="closeTooltipCard"
		/>
	</div>
</template>

<style lang="scss">
$marker-colors: (
	'black': #000000,
	'blue': #0072B2,
	'brown': #A0522D,
	'darkgray': #555555,
	'deep-purple': #4B0082,
	'gray': #757575,
	'green': #328725,
	'navy-blue': #001D5F,
	'orange': #F4801B,
	'purple': #792487,
	'red': #9F0000,
	'turquoise': #3B7BA0,
	'yellow': #7E7722
);

.owc-openkaarten-streetmap__map-canvas {
	block-size: 80dvh;
	inline-size: 100%;
	max-block-size: 661px;
}

.owc-openkaarten-streetmap {
	&__filters__body__list-item__dl-indicator {
		align-items: center;
		background-color: #0072B2;
		block-size: 30px;
		border-radius: 50%;
		display: flex;
		inline-size: 30px;
		justify-content: center;
		margin: 7px;
		min-inline-size: 30px;
		padding: 3px;

		@each $name, $color in $marker-colors {
			&.marker-#{$name} {
				background-color: #{$color};
				border-color: #{$color};
			}
		}
	}

	&__results {
		display: flex;
		flex-direction: column;
		gap: 8px;
		grid-area: results;

		&--loading {
			display: flex;
			justify-content: center;
			min-block-size: 30dvh;
			place-items: center;

			svg {
				max-inline-size: 3rem;
			}
		}
	}

	&__cluster-group {
		border-radius: 50%;

		&__circle {
			--owc-cluster-background: color-mix(
				in srgb,
				var(--owc-openkaarten-streetmap--cluster-color),
				rgba(255, 255, 255, 0.05) 75%
			);
			--owc-openkaarten-streetmap--cluster-color: #328725;
			align-items: center !important;
			background: var(--owc-cluster-background);
			block-size: 100%;
			border-radius: 50%;
			display: flex !important;
			inline-size: 100%;
			inset-block-start: 0;
			inset-inline-start: 0;
			justify-content: center !important;
			position: absolute;

			@each $name, $color in $marker-colors {
				&.marker-#{$name} {
					--owc-openkaarten-streetmap--cluster-color: #{$color};
				}
			}
		}

		&__count {
			font-weight: bold;
		}
	}

	&__map {
		border: 1px solid var(--Neutral-300, #E5E5E6);
		padding-block-start: 80px;
		position: relative;

		@media only screen and (min-width: 768px) {
			padding-block-start: 0;
		}

		.leaflet-marker-icon {
			&:focus-visible {
				aspect-ratio: 1;
				border-radius: 50%;
				outline-color: var(--owc-openkaarten-streetmap--primary-color);
			}

			&.dimmed {
				opacity: 0.4 !important;
				transition: opacity 0.2s ease-in-out, filter 0.2s ease-in-out;

				&:where(:hover, :focus-visible) {
					opacity: 1 !important;
				}
			}
		}

		.leaflet-custom-icon {
			&--inline-svg {
				align-items: center;
				background-color: #0072B2;
				border-radius: 100%;
				display: flex;
				justify-content: center;
				padding: 6px;

				.leaflet-svg {
					inline-size: 44px;

					&.fallback svg {
						margin: auto;
					}
				}

				&.active, &:where(:hover, :focus-visible) {
					border-radius: 50%;
					box-shadow: 0 0 0 3px white, 0 0 0 6px #0072B2;
					opacity: 1 !important;
					outline: none;
					transition: all 0.2s ease-in-out;
				}

				@each $name, $color in $marker-colors {
					&.marker-#{$name} {
						background-color: $color;

						&.active, &:where(:hover, :focus-visible) {
							box-shadow: 0 0 0 3px white, 0 0 0 6px $color;
						}
					}
				}

				svg path {
					fill: white;
				}
			}
		}

		&.filters-open {
			@media only screen and (min-width: 768px) {
				.leaflet-bottom.leaflet-right {
					transform: translateX(-282px);
					transition: transform 0.2s ease-in-out;
				}

				.leaflet-top.leaflet-left {
					transform: translateX(-145px);
					transition: transform 0.2s ease-in-out;
				}
			}
		}
	}

	&__overlay {
		background-color: var(--owc-map-overlay, rgba(0, 0, 0, 0.25));
		block-size: 100%;
		content: '';
		inline-size: 100%;
		inset-block-start: 0;
		inset-inline-start: 0;
		position: absolute;
		z-index: 999;

		@media only screen and (min-width: 768px) {
			display: none;
		}
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.2s ease-in-out;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.slide-enter-active,
	.slide-leave-active {
		transform: translateX(0);
		transition: transform 0.2s ease-in-out;
	}

	.slide-enter-from,
	.slide-leave-to {
		transform: translateX(120%);
	}

	&__controls {
		display: flex;
		gap: 0.5rem;
		inline-size: 100%;
		inset-block-start: 20px;
		inset-inline-start: 10px;
		position: absolute;
		transition: all 0.2s ease-in-out;
		z-index: 1000;

		@media only screen and (min-width: 768px) {
			inset-block-start: 1rem;
			inset-inline-start: 20px;
			max-inline-size: calc(100% - 287px);
			z-index: 9999;

			.filters-open & {
				max-inline-size: calc(100% - 435px);
			}
		}
	}
}
</style>
